import React, { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import { api } from "@/lib/api";
import { Loader2, Scissors, ChevronRight } from "lucide-react";

const STAGE_LABELS = {
    drafted_claude: "Drafted · Claude",
    checked_grok: "Checked · Grok",
    checked_copilot: "Checked · Copilot",
    checked_banana: "Checked · Banana",
    imaged_claude: "Imaged · Claude",
    approved_no6: "Approved · No. 6",
};

function PatternCard({ entry, stages, onMove }) {
    const s = entry.structured || {};
    const current = s.stage;
    const nextIdx = stages.indexOf(current) + 1;
    const next = nextIdx > 0 && nextIdx < stages.length ? stages[nextIdx] : null;

    return (
        <div className="paper-card p-3 tilt-l" data-testid={`pattern-card-${entry.id}`}>
            <div className="font-heading text-base uppercase tracking-[0.05em] text-ink mb-1">
                {s.name || entry.summary?.slice(0, 50) || "Untitled pattern"}
            </div>
            {s.pattern_code && (
                <div className="label-typed mb-1">{s.pattern_code} {s.sizing ? `· ${s.sizing}` : ""}</div>
            )}
            <div className="font-body text-xs text-ink-soft line-clamp-2">{entry.summary}</div>
            {next && (
                <button
                    onClick={() => onMove(entry.id, next)}
                    className="mt-2 inline-flex items-center gap-1 label-typed text-stamp-red hover:text-ink"
                    data-testid={`pattern-advance-${entry.id}`}
                >
                    advance to {STAGE_LABELS[next]} <ChevronRight className="w-3 h-3" />
                </button>
            )}
        </div>
    );
}

export default function Patterns() {
    const [data, setData] = useState(null);

    const load = () => api.get("/shed/patterns").then((r) => setData(r.data));
    useEffect(() => { load(); }, []);

    const move = async (id, stage) => {
        await api.patch(`/shed/patterns/${id}/stage`, { stage });
        load();
    };

    if (!data) return <Shell><div className="flex items-center gap-2 text-ink-soft label-typed"><Loader2 className="w-4 h-4 animate-spin" /> Opening the pattern drawer...</div></Shell>;

    return (
        <Shell>
            <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-typed tw-prefix">Ops Desk · Pattern Pipeline</div>
                    <h1 className="font-heading text-5xl uppercase tracking-[0.04em] text-ink mt-1 flex items-center gap-3">
                        <Scissors className="w-10 h-10 text-stamp-red" strokeWidth={1.5} />
                        Patterns<span className="cursor-blink" />
                    </h1>
                    <p className="font-body text-sm text-ink-soft mt-3 max-w-2xl">
                        {data.total} pattern{data.total === 1 ? "" : "s"} in the pipeline. Click advance to walk
                        a pattern one stage along the line. No. 6 signs off at the end.
                    </p>
                </div>
                <div className="pencil text-lg transform -rotate-2">
                    "Silly but world-appropriate." — No. 2
                </div>
            </div>

            {data.total === 0 ? (
                <div className="paper-card border border-dashed border-ink/30 p-16 text-center tilt-l" data-testid="patterns-empty">
                    <div className="font-heading text-3xl uppercase tracking-[0.06em] text-ink-faint">
                        No patterns yet
                    </div>
                    <div className="label-typed mt-3">
                        Shred a pattern brief through the Shredder — Claude will file it as `pattern` domain with a stage.
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-testid="pattern-board">
                    {data.stages.map((stage) => (
                        <div key={stage} className="space-y-3" data-testid={`column-${stage}`}>
                            <div className="label-typed tw-prefix pb-2 border-b-2 border-ink/30">
                                {STAGE_LABELS[stage]} · {data.by_stage[stage]?.length || 0}
                            </div>
                            {(data.by_stage[stage] || []).map((e) => (
                                <PatternCard key={e.id} entry={e} stages={data.stages} onMove={move} />
                            ))}
                            {(data.by_stage[stage] || []).length === 0 && (
                                <div className="text-ink-faint label-typed italic text-[10px]">empty</div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {data.unassigned?.length > 0 && (
                <div className="mt-8">
                    <div className="label-typed tw-prefix mb-2">Unassigned — stage missing</div>
                    <div className="grid md:grid-cols-3 gap-3">
                        {data.unassigned.map((e) => (
                            <PatternCard key={e.id} entry={e} stages={data.stages} onMove={move} />
                        ))}
                    </div>
                </div>
            )}
        </Shell>
    );
}
